import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'

import { type OptimizelyFlagKey, type OptimizelyFlagValue, OPTIMIZELY_FLAGS } from '@/types/optimizely-flags'

import { useOptimizelyFlagsInfo } from '@/lib/hooks/useOptimizelyFlagsInfo'

/**
 * Destination swaps owned by a running experiment, applied to CMS-authored CTAs.
 *
 * In code rather than Optimizely's visual editor on purpose: an injected href is a second source of
 * truth for navigation, invisible in review and left behind at wind-down.
 */
type CtaHrefOverride = {
  flag: OptimizelyFlagKey
  variant: OptimizelyFlagValue
  /** Dato `dataTestId` — picks out one CTA where several share a destination. */
  testId: string
  from: string
  to: string
}

/** Matched on testId *and* path: neither alone distinguishes CTAs that share a destination. */
const CTA_HREF_OVERRIDES: readonly CtaHrefOverride[] = [
  {
    flag: OPTIMIZELY_FLAGS.E045_V001_GHT_USER_JOURNEY_NAVIGATION,
    variant: 'variation-1',
    testId: 'homepage-product-guthealthtest',
    from: '/buymembership',
    to: '/buyguthealthtest',
  },
]

/**
 * Resolves a CMS CTA's href, swapping the destination when a live experiment owns it.
 */
export const useCtaHref = (url: string, testId?: string): string => {
  const { isFlagVariant } = useOptimizelyFlagsInfo()

  const override = testId ? CTA_HREF_OVERRIDES.find((entry) => entry.testId === testId) : undefined
  const hasMoved = Boolean(override && url !== override.from)

  useEffect(() => {
    if (!override || !hasMoved) return

    Sentry.captureMessage(
      `CTA href override '${override.testId}' is inert: expected ${override.from}, found ${url}`,
      'warning',
    )
  }, [override, hasMoved, url])

  if (!url || !override || hasMoved) return url

  return isFlagVariant(override.flag, override.variant) ? override.to : url
}
